import { AxiosInstance, AxiosResponse } from 'axios';
import { AnalyticsEventCategory, ContentCategory, trackEvent } from './google-analytics';

export type EntityOperationType = 'create' | 'update';

const EXCLUDED_TRACKING_PATTERNS = [
  '/token/',
  '/authorize',
  '/export',
  '/auth',
  '/login',
  '/logout',
  '/refresh',
  '/session',
];

export const hyphenToUnderscore = (str: string): string => str.replace(/-/g, '_');

export const COMMON_ENTITY_MAPPINGS: Record<string, string> = {
  'point-of-interest': 'poi',
  'point-of-interest-type': 'poi_type',
  'point-of-interest-set': 'poi_sets',
};

export const getEntityNameForAnalytics = (apiEntityPath: string): string => {
  return COMMON_ENTITY_MAPPINGS[apiEntityPath] ?? hyphenToUnderscore(apiEntityPath);
};

export const getContentCategory = (portal: string, entity: string): ContentCategory | null => {
  const entitySpecialCases: Record<string, string> = {
    ...COMMON_ENTITY_MAPPINGS,
    'account': 'accounts',
    'alert-condition': 'alert_conditions',
    'application': 'authorized_apps',
    'contract': 'contracts',
    'geofence-group': 'geofence_groups',
    'group': 'groups',
    'oidc-group-mapping': 'oidc_mapping',
    'person': 'people',
    'role': 'roles',
    'subscription': 'subscriptions',
    'thing': 'things',
    'transfer': 'transfers',
    'user-group': 'user_groups',
    'user': 'users',
    'alert': 'alerts',
    'analytics': 'analytics',
    'dashboard': 'dashboards',
    'report': 'reports',
  };

  const entitySuffix = entitySpecialCases[entity] || hyphenToUnderscore(entity);

  return `${portal}_${entitySuffix}` as ContentCategory;
};

export const extractEntityFromUrl = (url: string): string | null => {
  const match = url.match(/\/api\/v\d+\/(my\/)?([a-z-]+)/);
  return match ? match[2] : null;
};

export const extractEntityName = (url: string): string | null => {
  const entity = extractEntityFromUrl(url);
  return entity ? getEntityNameForAnalytics(entity) : null;
};

export const determineContentCategory = (url: string): ContentCategory | null => {
  const pathname = window.location.pathname;
  const hash = window.location.hash;
  const currentPath = pathname + hash;
  const portalMatch = currentPath.match(/\/(admin|track|manage)(?:[/#]|$)/);
  const portal = portalMatch ? portalMatch[1] : null;
  if (!portal) return null;

  const entity = extractEntityFromUrl(url);
  if (!entity) return null;

  return getContentCategory(portal, entity);
};

export const getOperationType = (method: string, url: string): EntityOperationType | null => {
  const upperMethod = method.toUpperCase();

  if (EXCLUDED_TRACKING_PATTERNS.some((pattern) => url.includes(pattern))) return null;

  if (upperMethod === 'POST') return 'create' as EntityOperationType;
  if (upperMethod === 'PUT' || upperMethod === 'PATCH') return 'update' as EntityOperationType;

  return null;
};

const trackedInstances = new WeakMap<AxiosInstance, number>();

export const addAnalyticsTracking = (axios: AxiosInstance): { axios: AxiosInstance; cleanup: () => void } => {
  const existingInterceptorId = trackedInstances.get(axios);
  if (existingInterceptorId !== undefined) {
    return {
      axios,
      cleanup: () => {
        axios.interceptors.response.eject(existingInterceptorId);
        trackedInstances.delete(axios);
      },
    };
  }

  const responseInterceptorId = axios.interceptors.response.use(
    (response: AxiosResponse) => {
      const config = response.config;
      const method = config?.method?.toUpperCase() || '';
      const url = config?.url || '';

      if (response.status >= 200 && response.status < 300) {
        const operationType = getOperationType(method, url);

        if (operationType) {
          const entityName = extractEntityName(url);
          const contentCategory = determineContentCategory(url);

          if (entityName && contentCategory) {
            const eventCategory = operationType === 'create'
              ? AnalyticsEventCategory.Created
              : AnalyticsEventCategory.Updated;

            trackEvent(eventCategory, entityName, contentCategory);
          }
        }
      }

      return response;
    },
    (error) => Promise.reject(error),
  );

  trackedInstances.set(axios, responseInterceptorId);

  return {
    axios,
    cleanup: () => {
      axios.interceptors.response.eject(responseInterceptorId);
      trackedInstances.delete(axios);
    },
  };
};
